import React, { useState, useCallback, useEffect } from 'react';
import {
  Card,
  Form,
  Button,
  Alert,
  Container,
  Spinner,
  Row,
  Col
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import SettingsPageHeader from '../components/SettingsPageHeader';
import { ThresholdIcon } from '../components/icons/SettingsIcons';
import { useAuth } from '../hooks/useAuth';
import { useRolePermissions } from '../hooks/useRolePermissions';
import { companyAPI, appearanceThresholdsAPI } from '../utils/api';
import type { Company } from '../types/auth';
import type { AppearanceThreshold } from '../types/appearanceThreshold';
import { formatDateTimeInCompanyTimezone } from '../utils/timezone';

const AppearanceThresholds: React.FC = () => {
  const { t } = useTranslation('pages', { keyPrefix: 'appearanceThresholds' });
  const { currentCompany, userRolePermissionsLoading } = useAuth();
  const { canAccessCompanyManagement } = useRolePermissions();

  const [companies, setCompanies] = useState<Company[]>([]);
  const [companiesLoading, setCompaniesLoading] = useState(true);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
  const [thresholdMinutes, setThresholdMinutes] = useState<number>(30);
  const [currentThreshold, setCurrentThreshold] = useState<AppearanceThreshold | null>(null);
  const [loading, setLoading] = useState(false);
  const [fetchingThreshold, setFetchingThreshold] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const settingsPath = currentCompany ? `/company/${currentCompany.company_id}/settings` : '/settings';

  const loadCompanies = useCallback(async () => {
    setCompaniesLoading(true);
    setError(null);
    try {
      const res = await companyAPI.getCompanies();
      const list = res?.data ?? [];
      setCompanies(list);
      if (list.length > 0) {
        const preferred = currentCompany && list.some((c) => c.company_id === currentCompany.company_id)
          ? currentCompany.company_id
          : list[0].company_id;
        setSelectedCompanyId((prev) => prev || preferred);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      setError(err.response?.data?.message || err.message || t('loadCompaniesFailed'));
    } finally {
      setCompaniesLoading(false);
    }
  }, [currentCompany]);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  const loadThreshold = useCallback(async (companyId: string) => {
    if (!companyId) {
      setCurrentThreshold(null);
      return;
    }
    setFetchingThreshold(true);
    setError(null);
    try {
      const res = await appearanceThresholdsAPI.getThreshold(companyId);
      setCurrentThreshold(res.threshold ?? null);
      if (res.threshold) {
        setThresholdMinutes(res.threshold.threshold_minutes);
      } else {
        setThresholdMinutes(30);
      }
    } catch (e: unknown) {
      const err = e as { response?: { status?: number; data?: { message?: string } }; message?: string };
      if (err.response?.status === 404) {
        setCurrentThreshold(null);
        setThresholdMinutes(30);
      } else {
        setError(err.response?.data?.message || err.message || t('loadThresholdFailed'));
      }
    } finally {
      setFetchingThreshold(false);
    }
  }, []);

  useEffect(() => {
    if (selectedCompanyId) {
      loadThreshold(selectedCompanyId);
    } else {
      setCurrentThreshold(null);
    }
  }, [selectedCompanyId, loadThreshold]);

  const handleCompanyChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = e.target.value;
    setSelectedCompanyId(id);
    setSuccess(null);
    setError(null);
  }, []);

  const handleThresholdChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseInt(e.target.value, 10);
    if (!Number.isNaN(val) && val >= 0) {
      setThresholdMinutes(val);
    }
    setSuccess(null);
    setError(null);
  }, []);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCompanyId.trim()) {
      setError(t('selectCompanyError'));
      return;
    }
    if (thresholdMinutes < 0) {
      setError(t('thresholdError'));
      return;
    }
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await appearanceThresholdsAPI.saveThreshold({
        company_id: selectedCompanyId,
        threshold_minutes: thresholdMinutes
      });
      setSuccess(res.message || t('saveSuccess'));
      setCurrentThreshold(res.threshold ?? null);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string; errors?: Record<string, string[]> } }; message?: string };
      const msg = err.response?.data?.message || err.message || t('saveFailed');
      const errors = err.response?.data?.errors;
      const detail = errors ? Object.values(errors).flat().join(' ') : '';
      setError(detail ? `${msg}: ${detail}` : msg);
    } finally {
      setLoading(false);
    }
  }, [selectedCompanyId, thresholdMinutes]);

  if (userRolePermissionsLoading) {
    return (
      <Layout title={t('title')}>
        <Container className="text-center an-enterprise-page">
          <Spinner animation="border" variant="primary" />
        </Container>
      </Layout>
    );
  }

  if (!canAccessCompanyManagement) {
    return (
      <Layout title={t('title')}>
        <Container className="an-enterprise-page">
          <Alert variant="danger" role="alert">
            {t('noAccess')}
          </Alert>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout title={t('title')}>
      <Container fluid className="an-enterprise-page an-data-page an-settings-page an-page-shell">
        <SettingsPageHeader
          title={t('title')}
          settingsPath={settingsPath}
        />

        <Card className="shadow-sm an-panel an-settings-form-card">
          <Card.Body>
            <div className="d-flex align-items-center mb-4">
              <ThresholdIcon />
              <h5 className="fw-medium text-primary mb-0">{t('manageTitle')}</h5>
            </div>

            <p className="text-muted small mb-4">
              {t('description')}
            </p>

            {success && (
              <Alert variant="success" role="status" onClose={() => setSuccess(null)} dismissible>
                {success}
              </Alert>
            )}

            {error && (
              <Alert variant="danger" role="alert" onClose={() => setError(null)} dismissible>
                {error}
              </Alert>
            )}

            {companiesLoading ? (
              <div className="d-flex align-items-center gap-2 py-3">
                <Spinner animation="border" size="sm" />
                <span className="text-muted">{t('loadingCompanies')}</span>
              </div>
            ) : companies.length === 0 ? (
              <Alert variant="info">
                {t('noCompanies')}
              </Alert>
            ) : (
              <Form onSubmit={handleSubmit}>
                <Row className="g-3">
                  <Col xs={12} md={6}>
                    <Form.Group>
                      <Form.Label className="fw-bold text-dark">{t('companyLabel')} <span className="text-danger">*</span></Form.Label>
                      <Form.Select
                        value={selectedCompanyId}
                        onChange={handleCompanyChange}
                        aria-label={t('selectCompanyAriaLabel')}
                      >
                        <option value="">{t('selectCompany')}</option>
                        {companies.map((c) => (
                          <option key={c.company_id} value={c.company_id}>
                            {c.name} ({c.company_id})
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Group>
                      <Form.Label className="fw-bold text-dark">{t('thresholdLabel')} <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="number"
                        min={0}
                        step={1}
                        value={thresholdMinutes}
                        onChange={handleThresholdChange}
                        placeholder={t('thresholdPlaceholder')}
                        aria-label={t('thresholdAriaLabel')}
                        disabled={!selectedCompanyId || fetchingThreshold}
                      />
                      <Form.Text className="text-muted">
                        {t('thresholdHint')}
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                {fetchingThreshold && selectedCompanyId && (
                  <div className="d-flex align-items-center gap-2 mt-2 small text-muted">
                    <Spinner animation="border" size="sm" />
                    {t('loadingThreshold')}
                  </div>
                )}

                {!fetchingThreshold && currentThreshold && selectedCompanyId && (
                  <div className="mt-3 p-3 bg-light rounded small">
                    <strong>{t('currentThreshold')}</strong> {currentThreshold.threshold_minutes} {t('minutes')}
                    {currentThreshold.updated_at && (
                      <span className="text-muted ms-2">
                        ({t('lastUpdated', { date: formatDateTimeInCompanyTimezone(
                          currentThreshold.updated_at, 
                          companies.find(c => c.company_id === selectedCompanyId)?.timezone || currentCompany?.timezone || 'UTC'
                        ) })})
                      </span>
                    )}
                  </div>
                )}

                <div className="d-flex justify-content-end mt-4">
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={loading || !selectedCompanyId || companiesLoading || fetchingThreshold}
                  >
                    {loading ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        {t('saving')}
                      </>
                    ) : (
                      <>
                        <ThresholdIcon />
                        {t('saveThreshold')}
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            )}
          </Card.Body>
        </Card>
      </Container>
    </Layout>
  );
};

export default AppearanceThresholds;
